import { addDefaultCountryCodeToPhoneNumber } from '@/app/(main)/air-request/utils/addDefaultCountryCodeToPhoneNumber';
import { formContacts } from '@/app/(main)/client-detail/utils/contacts';
import type { GetPassportClientData } from '@/types/clientDetailsApiTypes';
import {
  type ClientDocumentWithPassportData,
  type CreateClientFormData,
  type GetClientData,
  type ClassPreference,
  type InterestPreference,
  type DrinkingPreference,
  type PillowPreference,
  type RoomPreference,
  type SpaPreference,
  type GetRelatedPassengersData,
  type RelatedPassengerFormData,
  type ClientPreferencesOptionType,
  type RelationshipType,
} from '@/types/clientsApiTypes';
import { formatYYYYMMDDToMMDDYYYY } from '@/utils/formatDate';
import {
  combineStayAccommodationRawFromPreferenceStay,
  flattenStayAccommodationTypesForForm,
} from '@/utils/preferences/partitionAccommodationTypes';
import { replaceDraftData } from '../replaceMockData';

const getDefaultSystem = (
  usHeight: string | null,
  usWeight: string | null,
  metricHeight: string | null,
  metricWeight: string | null
): 'us' | 'metric' => {
  const cleanUsHeight = usHeight?.trim() || '';
  const cleanUsWeight = usWeight?.trim() || '';
  const cleanMetricHeight = metricHeight?.trim() || '';
  const cleanMetricWeight = metricWeight?.trim() || '';

  const hasUsHeight = !!cleanUsHeight;
  const hasUsWeight = !!cleanUsWeight;
  const hasMetricHeight = !!cleanMetricHeight;
  const hasMetricWeight = !!cleanMetricWeight;

  const usComplete = hasUsHeight && hasUsWeight;
  const metricComplete = hasMetricHeight && hasMetricWeight;

  if (metricComplete && !usComplete) {
    return 'metric';
  }

  if (usComplete) {
    return 'us';
  }

  const hasAnyMetric = hasMetricHeight || hasMetricWeight;
  const hasAnyUs = hasUsHeight || hasUsWeight;

  if (hasAnyMetric && !hasAnyUs) {
    return 'metric';
  }

  return 'us';
};

export const getDefaultFormData = (
  clientData?: GetClientData,
  documentsData?: ClientDocumentWithPassportData[],
  relatedPassengersData?: GetRelatedPassengersData[],
  passportData?: GetPassportClientData[]
): CreateClientFormData => {
  const primaryContacts = formContacts(clientData);

  return {
    primary_passenger: {
      client: {
        client_id: clientData?.id ?? 0,
        userHasCertifiedTheInformation: clientData?.userHasCertifiedTheInformation ?? false,
        preferred_name: clientData?.preferred_name ?? null,
        first_name: replaceDraftData(clientData?.first_name ?? '') ?? '',
        middle_name: clientData?.middle_name ?? null,
        last_name: replaceDraftData(clientData?.last_name ?? '') ?? '',
        is_vip: clientData?.is_vip ?? false,
        gender: clientData?.gender ?? '',
        source_id: 0,
        email: primaryContacts.email,
        additional_emails: primaryContacts.additional_emails,
        crm_emails: clientData?.emails ?? [],
        crm_phones: clientData?.phones ?? [],
        date_of_birth: formatYYYYMMDDToMMDDYYYY(clientData?.date_of_birth ?? '') ?? '',
        phone: primaryContacts.phone ? addDefaultCountryCodeToPhoneNumber(primaryContacts.phone) : '',
        client_mailing_address: clientData?.client_mailing_address ?? '',
        client_mailing_address_line_2: clientData?.client_mailing_address_line_2 ?? null,
        client_mailing_city: clientData?.client_mailing_city ?? '',
        client_mailing_state: clientData?.client_mailing_state ?? null,
        client_mailing_country: clientData?.client_mailing_country ?? '',
        zip_code: clientData?.zip_code ?? '',
        social_facebook_url: clientData?.social_facebook_url ?? null,
        social_instagram_url: clientData?.social_instagram_url ?? null,
        social_linkedin_url: clientData?.social_linkedin_url ?? null,
        social_twitter_url: clientData?.social_twitter_url ?? null,
        social_custom_url: clientData?.social_custom_url ?? null,
        is_billing_same_as_mailing: clientData?.is_billing_same_as_mailing ?? false,
        client_billing_address: clientData?.client_billing_address ?? null,
        client_billing_address_line_2: clientData?.client_billing_address_line_2 ?? null,
        client_billing_city: clientData?.client_billing_city ?? null,
        client_billing_state: clientData?.client_billing_state ?? null,
        client_billing_country: clientData?.client_billing_country ?? null,
        billing_zip_code: clientData?.billing_zip_code ?? null,
        phones: primaryContacts.phones.map((phone) => ({
          phone: phone.phone ? addDefaultCountryCodeToPhoneNumber(phone.phone) : '',
          title: phone.title,
        })),
        profile_picture_url: clientData?.profile_picture_url ?? null,
        preferred_airport_ids: clientData?.preferred_airports?.map((airport) => airport.id) ?? [],
        profile_picture_storage_id: null,
        emergency_contact_name: clientData?.emergency_contact_name ?? '',
        emergency_relationship_type_id: clientData?.emergency_relationship?.id ?? null,
        emergency_relationship: clientData?.emergency_relationship
          ? { label: clientData?.emergency_relationship.description, value: clientData?.emergency_relationship.id }
          : null,
        emergency_contact_phone: clientData?.emergency_contact_phone ?? '',
        emergency_contact_email: clientData?.emergency_contact_email ?? '',
        relationship: (clientData?.relationship?.value as RelationshipType) ?? null,
      },
      preference_data: {
        allergies: getArrayAsString(clientData?.preferences?.preference_data?.allergies) ?? null,
        // Flight preferences - direct mapping with nested structure
        fly: {
          preferred_private_aviation_company:
            clientData?.preferences?.preference_data?.fly?.preferred_private_aviation_company ?? null,
          fly_private_aviation: clientData?.preferences?.preference_data?.fly?.fly_private_aviation ?? null,
          seat: clientData?.preferences?.preference_data?.fly?.seat ?? 'Window',
          class_preference: getPreferencesKeys(
            clientData?.preferences?.preference_data?.fly?.class_preference
          ) as ClassPreference[],
        },

        // Travel preferences - direct mapping
        travel: clientData?.preferences?.preference_data.travel ?? null,

        // Stay preferences - direct mapping with nested structure
        stay: {
          accommodation_types: (() => {
            const preferenceStay = clientData?.preferences?.preference_data?.stay;
            const rawForFlatten =
              combineStayAccommodationRawFromPreferenceStay(preferenceStay) ??
              (clientData?.preferences?.preference_data.stay?.accommodation_types != null
                ? (clientData.preferences.preference_data.stay.accommodation_types as unknown[])
                : null);
            return rawForFlatten != null ? flattenStayAccommodationTypesForForm(rawForFlatten) : null;
          })(),
          preferences_note: clientData?.preferences?.preference_data.stay?.preferences_note ?? null,
        },

        // Lifestyle preferences - direct mapping (backend sends keys)
        general_interests: getPreferencesKeys(
          clientData?.preferences?.preference_data.general_interests
        ) as InterestPreference[],
        beverage_preferences: getPreferencesKeys(
          clientData?.preferences?.preference_data?.beverage_preferences
        ) as DrinkingPreference[],
        pillow_preferences: getPreferencesKeys(
          clientData?.preferences?.preference_data.pillow_preferences
        ) as PillowPreference[],
        room_configuration_preferences: getPreferencesKeys(
          clientData?.preferences?.preference_data.room_configuration_preferences
        ) as RoomPreference[],
        spa_preferences: getPreferencesKeys(
          clientData?.preferences?.preference_data.spa_preferences
        ) as SpaPreference[],
        spa_preferences_special_requests:
          clientData?.preferences?.preference_data.spa_preferences_special_requests ?? null,
        likes: clientData?.preferences?.preference_data.likes ?? null,
        dislikes: clientData?.preferences?.preference_data.dislikes ?? null,

        personal: {
          personal_metric_system: getDefaultSystem(
            clientData?.preferences?.preference_data.personal?.comfort_related?.us?.height ?? null,
            clientData?.preferences?.preference_data.personal?.comfort_related?.us?.weight ?? null,
            clientData?.preferences?.preference_data.personal?.comfort_related?.metric?.height ?? null,
            clientData?.preferences?.preference_data.personal?.comfort_related?.metric?.weight ?? null
          ),
          information: clientData?.preferences?.preference_data.personal?.information ?? null,
          comfort_related: {
            us: {
              height: clientData?.preferences?.preference_data.personal?.comfort_related?.us?.height ?? null,
              weight: clientData?.preferences?.preference_data.personal?.comfort_related?.us?.weight ?? null,
            },
            metric: {
              height: clientData?.preferences?.preference_data.personal?.comfort_related?.metric?.height ?? null,
              weight: clientData?.preferences?.preference_data.personal?.comfort_related?.metric?.weight ?? null,
            },
          },
        },
      },
      tsa: {
        known_traveler_number: clientData?.tsa?.known_traveler_number ?? null,
        known_traveler_number_expire_date:
          formatYYYYMMDDToMMDDYYYY(clientData?.tsa?.known_traveler_number_expire_date ?? '') ?? null,
        global_entry: clientData?.tsa?.global_entry ?? null,
        global_expiry_date: formatYYYYMMDDToMMDDYYYY(clientData?.tsa?.global_expiry_date ?? '') ?? null,
      },
      passport: {
        passport_number: passportData?.at(0)?.passport_number ?? null,
        expiry_date: formatYYYYMMDDToMMDDYYYY(passportData?.at(0)?.expiry_date ?? '') ?? null,
        passport_id: passportData?.at(0)?.id ?? null,
      },
      documents:
        documentsData?.map((document) => ({
          client_full_name: clientData?.full_name ?? '',
          id: document.id,
          document_name: document.document_name,
          document_type: document.document_type as 'passport' | 'id',
          url: document.document_url ?? null,
          passport_number: document.passport_number ?? null,
          expiry_date: formatYYYYMMDDToMMDDYYYY(document.expiry_date ?? '') ?? null,
          issuance_country: document.issuance_country ?? null,
          issue_date: formatYYYYMMDDToMMDDYYYY(document.issue_date ?? '') ?? null,
          client_id: clientData?.id ?? 0,
          document_url: document.document_url ?? null,
          created_at: document.created_at ?? null,
          updated_at: document.updated_at ?? null,
        })) ?? [],
      selected_airports:
        clientData?.preferred_airports?.map((airport) => ({
          value: airport.id,
          label: airport.code ?? airport.city,
        })) ?? [],
      loyalty_numbers:
        clientData?.loyalty_numbers?.map((loyalty) => ({
          lp_id: loyalty.id,
          is_legacy: loyalty.is_legacy ?? false,
          category_id: loyalty.category?.id ?? null,
          number: loyalty.number,
          loyalty_program: loyalty.loyalty_program,
          frequent_flyer_number: loyalty.frequent_flyer_number,
          note: loyalty.note,
        })) ?? [],
    },
    related_passengers:
      relatedPassengersData?.map((passenger): RelatedPassengerFormData => {
        // Auto-detect if passenger address matches primary passenger address
        const addressMatchesPrimary = addressesMatch(passenger, clientData);
        const passengerContacts = formContacts(passenger);

        return {
          client: {
            client_id: passenger.id ?? 0,
            userHasCertifiedTheInformation: passenger.userHasCertifiedTheInformation ?? false,
            preferred_name: passenger.preferred_name ?? null,
            first_name: replaceDraftData(passenger.first_name ?? '') ?? '',
            middle_name: passenger.middle_name ?? null,
            last_name: replaceDraftData(passenger.last_name ?? '') ?? '',
            gender: passenger.gender ?? '',
            relationship: (passenger.relationship?.value as RelationshipType) ?? null,
            contact_primary_client_id: passenger.contact_primary_client_id ?? null,
            date_of_birth: formatYYYYMMDDToMMDDYYYY(passenger.date_of_birth ?? '') ?? '',
            email: passengerContacts.email,
            phone: passengerContacts.phone ? addDefaultCountryCodeToPhoneNumber(passengerContacts.phone) : '',
            client_mailing_address: passenger.client_mailing_address ?? '',
            client_mailing_address_line_2: passenger.client_mailing_address_line_2 ?? '',
            client_mailing_city: passenger.client_mailing_city ?? '',
            client_mailing_state: passenger.client_mailing_state ?? '',
            client_mailing_country: passenger.client_mailing_country ?? '',
            zip_code: passenger.zip_code ?? '',
            same_as_primary: addressMatchesPrimary || false,
            additional_emails: passengerContacts.additional_emails,
            crm_emails: passenger.emails ?? [],
            crm_phones: passenger.phones ?? [],
            phones: passengerContacts.phones.map((phone) => ({
              phone: phone.phone ? addDefaultCountryCodeToPhoneNumber(phone.phone) : '',
              title: phone.title,
            })),
          },
          tsa: {
            global_entry: passenger.tsa?.global_entry ?? null,
            global_expiry_date: formatYYYYMMDDToMMDDYYYY(passenger.tsa?.global_expiry_date ?? '') ?? null,
            known_traveler_number: passenger.tsa?.known_traveler_number ?? null,
            known_traveler_number_expire_date:
              formatYYYYMMDDToMMDDYYYY(passenger.tsa?.known_traveler_number_expire_date ?? '') ?? null,
          },
          passport: {
            passport_number: null,
            expiry_date: null,
            passport_id: null,
          },
          preference_data: {
            allergies: getArrayAsString(passenger.preferences?.preference_data?.allergies) ?? null,
            fly: {
              seat: passenger.preferences?.preference_data?.fly?.seat ?? null,
              allergies: '',
              preferred_private_aviation_company:
                passenger.preferences?.preference_data?.fly?.preferred_private_aviation_company ?? null,
              fly_private_aviation: passenger.preferences?.preference_data?.fly?.fly_private_aviation ?? null,
              class_preference: getPreferencesKeys(
                passenger.preferences?.preference_data?.fly?.class_preference
              ) as ClassPreference[],
            },
            personal: {
              personal_metric_system: getDefaultSystem(
                passenger.preferences?.preference_data.personal?.comfort_related?.us?.height ?? null,
                passenger.preferences?.preference_data.personal?.comfort_related?.us?.weight ?? null,
                passenger.preferences?.preference_data.personal?.comfort_related?.metric?.height ?? null,
                passenger.preferences?.preference_data.personal?.comfort_related?.metric?.weight ?? null
              ),
              comfort_related: {
                us: {
                  height: passenger.preferences?.preference_data.personal?.comfort_related?.us?.height ?? null,
                  weight: passenger.preferences?.preference_data.personal?.comfort_related?.us?.weight ?? null,
                },
                metric: {
                  height: passenger.preferences?.preference_data.personal?.comfort_related?.metric?.height ?? null,
                  weight: passenger.preferences?.preference_data.personal?.comfort_related?.metric?.weight ?? null,
                },
              },
              information: passenger.preferences?.preference_data.personal?.information ?? null,
            },
          },
          documents: [],
          loyalty_numbers:
            passenger?.loyalty_numbers?.map((loyalty) => ({
              lp_id: loyalty.id,
              category_id: loyalty.category?.id ?? null,
              number: loyalty.number ?? '',
              is_legacy: loyalty.is_legacy ?? false,
              loyalty_program: loyalty.loyalty_program ?? '',
              frequent_flyer_number: loyalty.frequent_flyer_number,
              note: loyalty.note,
            })) ?? [],
        };
      }) ?? [],
  };
};

const getPreferencesKeys = (preferences?: ClientPreferencesOptionType[] | null) => {
  return (
    preferences?.map(
      (preference) =>
        preference.value as
          | InterestPreference
          | DrinkingPreference
          | PillowPreference
          | RoomPreference
          | SpaPreference
          | ClassPreference
    ) ?? null
  );
};

const getArrayAsString = (array?: string[] | null | string) => {
  if (Array.isArray(array)) {
    return array.join(', ') ?? null;
  }
  return array ?? null;
};

function areEqualForAddress(a?: string | null, b?: string | null): boolean {
  return a === b || (a == null && b === '') || (a === '' && b == null);
}

function addressesMatch(p?: GetRelatedPassengersData, c?: GetClientData): boolean {
  return (
    areEqualForAddress(p?.client_mailing_address, c?.client_mailing_address) &&
    areEqualForAddress(p?.client_mailing_address_line_2, c?.client_mailing_address_line_2) &&
    areEqualForAddress(p?.client_mailing_city, c?.client_mailing_city) &&
    areEqualForAddress(p?.client_mailing_state, c?.client_mailing_state) &&
    areEqualForAddress(p?.client_mailing_country, c?.client_mailing_country) &&
    areEqualForAddress(p?.zip_code, c?.zip_code)
  );
}
